import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMagazzinoVendite, getMagazzinoVenditeBigBags,
  type MagazzinoVenditeResponse, type BigBagDisponibileDashboard,
} from '../../api/dashboard';
import Modal from '../ui/Modal';
import { fmtKg } from '../../lib/saldo';

/** Riga aperta nel modale: il materiale serve al backend insieme alla tipologia. */
interface RigaAperta {
  materiale: string;
  tipologiaId: string | null;
  nome: string;
}

/**
 * Magazzino vendite: cosa c'è DA VENDERE, non cosa è stato prodotto.
 *
 * È la differenza con l'Andamento qui sopra: lì un sacco uscito continua a
 * contare (è stato prodotto in quel mese), qui sparisce appena sale sul camion.
 *
 * Due livelli — materiale/CER come intestazione, dentro le sue tipologie —
 * perché è così che si vende: il compratore chiede «quanto alluminio FV avete»,
 * non «quanto 19 12 03».
 */
export default function MagazzinoVenditeSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dati, setDati] = useState<MagazzinoVenditeResponse | null>(null);
  const [errore, setErrore] = useState(false);
  const [aperta, setAperta] = useState<RigaAperta | null>(null);
  const [sacchi, setSacchi] = useState<BigBagDisponibileDashboard[] | null>(null);

  const carica = useCallback(() => {
    void (async () => {
      try { setDati(await getMagazzinoVendite()); setErrore(false); }
      catch (e) { console.error('dashboard/magazzino-vendite', e); setErrore(true); }
    })();
  }, []);
  useEffect(() => { carica(); }, [carica]);

  function apri(materiale: string, tipologiaId: string | null, nome: string) {
    setAperta({ materiale, tipologiaId, nome });
    setSacchi(null);
    void (async () => {
      try { setSacchi(await getMagazzinoVenditeBigBags(materiale, tipologiaId)); }
      catch (e) { console.error('dashboard/magazzino-vendite/big-bags', e); setSacchi([]); }
    })();
  }

  if (errore) return null;   // come le altre card: niente widget rotto in Dashboard

  return (
    <section data-testid="magazzino-vendite">
      <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">
        {t('dashboard.magazzino.titolo')}
      </h2>
      <div className="bg-white border border-stone-mid rounded-xl p-4">
        {!dati ? (
          <p className="text-sm text-ink-soft">{t('dashboard.magazzino.caricamento')}</p>
        ) : dati.gruppi.length === 0 ? (
          /* Stato vuoto ESPLICITO: una sezione vuota sembra un guasto. */
          <p className="text-sm text-ink-faint" data-testid="magazzino-vuoto">
            {t('dashboard.magazzino.vuoto')}
          </p>
        ) : (
          <>
            <p className="text-xs text-ink-soft mb-3" data-testid="magazzino-totale">
              {t('dashboard.magazzino.totale', {
                kg: fmtKg(dati.kg_totali), n: dati.big_bags_totali })}
            </p>
            <div className="space-y-4">
              {dati.gruppi.map((g) => (
                <div key={g.materiale} data-testid={`magazzino-gruppo-${g.materiale}`}>
                  <div className="flex items-baseline justify-between border-b border-stone-mid pb-1 mb-1">
                    <h3 className="text-sm font-semibold text-ink-dark">{g.etichetta}</h3>
                    <span className="text-xs text-ink-soft">
                      {t('dashboard.magazzino.gruppo_totale', {
                        kg: fmtKg(g.kg), n: g.big_bags })}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {g.tipologie.map((tp) => (
                        <tr key={tp.tipologia_id ?? '__nessuna__'}
                          className="border-b border-stone-mid/40 last:border-0">
                          <td className="py-1">
                            <button type="button"
                              data-testid={`magazzino-riga-${g.materiale}-${tp.tipologia_id ?? 'nessuna'}`}
                              onClick={() => apri(g.materiale, tp.tipologia_id, tp.tipologia_nome)}
                              className="text-sage-dark hover:underline">
                              {tp.tipologia_nome}
                            </button>
                          </td>
                          <td className="py-1 text-right font-medium w-28">{fmtKg(tp.kg)} kg</td>
                          <td className="py-1 text-right text-ink-mid w-28">
                            {t('dashboard.magazzino.n_big_bags', { n: tp.big_bags })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {aperta && (
        <Modal
          title={t('dashboard.magazzino.modale_titolo', { tipologia: aperta.nome })}
          testid="magazzino-sacchi"
          onClose={() => setAperta(null)}
        >
          {!sacchi ? (
            <p className="text-sm text-ink-soft">{t('dashboard.magazzino.caricamento')}</p>
          ) : sacchi.length === 0 ? (
            <p className="text-sm text-ink-faint" data-testid="magazzino-sacchi-vuoto">
              {t('dashboard.magazzino.sacchi_vuoto')}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  <th className="py-1.5">{t('dashboard.magazzino.col_token')}</th>
                  <th className="py-1.5 text-right">{t('dashboard.magazzino.col_peso')}</th>
                  <th className="py-1.5">{t('dashboard.magazzino.col_area')}</th>
                  <th className="py-1.5">{t('dashboard.magazzino.col_data')}</th>
                </tr>
              </thead>
              <tbody>
                {sacchi.map((b) => (
                  <tr key={b.id} className="border-t border-stone-mid/60">
                    <td className="py-1.5">
                      {/* Porta alla SCHEDA del sacco: è la pagina che si apre
                          anche scansionando il QR in piazzale. */}
                      <button type="button" data-testid={`magazzino-sacco-${b.qr_token}`}
                        onClick={() => navigate(`/big-bags/${b.qr_token}`)}
                        className="font-mono text-xs text-sage-dark hover:underline">
                        {b.qr_token.slice(0, 8)}
                      </button>
                    </td>
                    <td className="py-1.5 text-right">{fmtKg(b.peso_kg)}</td>
                    <td className="py-1.5">{b.area_codice ?? '—'}</td>
                    <td className="py-1.5 text-ink-mid">
                      {b.data_produzione
                        ? new Date(b.data_produzione).toLocaleDateString('it-IT') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </section>
  );
}
